import { NavLink } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Music2 } from 'lucide-react';
import { theme } from '@/styles/theme';

const NavList = styled.ul<{ $mobile?: boolean }>`
  display: flex;
  flex-direction: ${({ $mobile }) => ($mobile ? 'column' : 'row')};
  align-items: ${({ $mobile }) => ($mobile ? 'flex-start' : 'center')};
  gap: ${({ $mobile }) => ($mobile ? 'clamp(0.75rem, 1.5vh, 1rem)' : theme.spacing[6])};
  list-style: none;
  margin: 0;
  padding: 0;
  width: 100%;
`;

const NavItem = styled(motion.li)`
  width: 100%;
`;

const NavLinkWrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

const MusicIcon = styled(Music2)`
  position: absolute;
  left: -20px;
  opacity: 0;
  transform: scale(0) rotate(-15deg);
  transition: all ${theme.transitions.base};
  color: ${theme.colors.primary.gold};
  filter: drop-shadow(0 0 4px rgba(212, 175, 55, 0.6));

  @media (prefers-reduced-motion: reduce) {
    transition: opacity ${theme.transitions.base};
    transform: none !important;
  }
`;

const StyledNavLink = styled(NavLink)<{ $mobile?: boolean; $onDark?: boolean }>`
  font-family: ${theme.fonts.body};
  font-size: ${({ $mobile }) => ($mobile ? 'clamp(1rem, 2.5vh, 1.125rem)' : theme.fontSizes.base)};
  font-weight: ${({ $mobile }) => ($mobile ? theme.fontWeights.semibold : theme.fontWeights.medium)};
  color: ${({ $mobile, $onDark }) =>
    $mobile
      ? theme.colors.primary.navy
      : $onDark
        ? 'rgba(255, 255, 255, 0.92)'
        : theme.colors.primary.darkBlue};
  text-shadow: ${({ $mobile, $onDark }) =>
    !$mobile && $onDark ? '0 1px 8px rgba(16, 19, 31, 0.45)' : 'none'};
  text-decoration: none;
  position: relative;
  padding: ${({ $mobile }) => ($mobile ? 'clamp(0.5rem, 1.5vh, 0.75rem) 0.5rem' : `${theme.spacing[2]} 0`)};
  transition: all ${theme.transitions.smooth};
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  white-space: nowrap;
  border-radius: ${theme.borderRadius.md};

  &::after {
    content: '';
    position: absolute;
    bottom: ${({ $mobile }) => ($mobile ? theme.spacing[2] : '0')};
    left: ${({ $mobile }) => ($mobile ? theme.spacing[2] : '0')};
    width: 0;
    height: 2px;
    background: ${theme.colors.primary.gold};
    transition: width ${theme.transitions.base};
  }

  /* Hover only on devices that actually hover: on touch screens iOS plays
     the hover state on the first tap and needs a second tap for the click. */
  @media (hover: hover) {
    &:hover {
      color: ${theme.colors.primary.gold};
      background: ${({ $mobile }) => ($mobile ? 'rgba(212, 175, 55, 0.08)' : 'transparent')};

      &::after {
        width: ${({ $mobile }) => ($mobile ? `calc(100% - ${theme.spacing[4]})` : '100%')};
      }
    }
  }

  &:active {
    color: ${theme.colors.primary.gold};
  }

  &.active {
    color: ${({ $mobile }) => ($mobile ? theme.colors.primary.navy : theme.colors.primary.gold)};
    font-weight: ${theme.fontWeights.semibold};
    background: ${({ $mobile }) => ($mobile ? 'rgba(15, 52, 96, 0.08)' : 'rgba(212, 175, 55, 0.10)')};
    border-radius: ${({ $mobile }) => ($mobile ? theme.borderRadius.md : theme.borderRadius.lg)};

    &::after {
      width: ${({ $mobile }) => ($mobile ? `calc(100% - ${theme.spacing[4]})` : '100%')};
      background: ${theme.colors.primary.gold};
      height: 3px;
    }

    /* Show music icon on active link (desktop only) */
    @media (min-width: ${theme.breakpoints.lg}) {
      padding: ${theme.spacing[2]} ${theme.spacing[3]};

      ${MusicIcon} {
        opacity: 1;
        transform: scale(1) rotate(0deg);
      }
    }
  }

  @media (prefers-reduced-motion: reduce) {
    transition: color ${theme.transitions.base}, background ${theme.transitions.base};
  }
`;

interface NavigationProps {
  mobile?: boolean;
  onNavigate?: () => void;
  /** Render light-on-dark (desktop nav sitting over the transparent hero header). */
  onDark?: boolean;
}

const navItems = [
  { path: '/', label: 'Start' },
  { path: '/over-ons', label: 'Over Ons' },
  { path: '/groepen', label: 'Groepen' },
  { path: '/evenementen', label: 'Evenementen' },
  { path: '/berichten', label: 'Berichten' },
  { path: '/media', label: 'Gallerij' },
  { path: '/sponsors', label: 'Sponsors' },
  { path: '/contact', label: 'Contact' },
  { path: '/member', label: 'Ledenportaal' },
];

const navItemVariants = {
  hidden: { opacity: 0, y: -10 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.05,
      duration: 0.3,
      ease: 'easeOut',
    },
  }),
};

// Tight stagger: the full list settles in ~0.45s so every link is stationary
// (and reliably tappable) almost immediately after the panel lands.
const mobileNavItemVariants = {
  hidden: { opacity: 0, x: -12 },
  visible: (i: number) => ({
    opacity: 1,
    x: 0,
    transition: {
      delay: 0.05 + i * 0.025,
      duration: 0.22,
      ease: 'easeOut',
    },
  }),
};

export const Navigation = ({ mobile = false, onNavigate, onDark = false }: NavigationProps) => {
  return (
    <NavList $mobile={mobile}>
      {navItems.map((item, index) => (
        <NavItem
          key={item.path}
          custom={index}
          initial="hidden"
          animate="visible"
          variants={mobile ? mobileNavItemVariants : navItemVariants}
        >
          <NavLinkWrapper>
            {!mobile && <MusicIcon size={16} />}
            <StyledNavLink
              to={item.path}
              $mobile={mobile}
              $onDark={onDark}
              onClick={onNavigate}
            >
              {item.label}
            </StyledNavLink>
          </NavLinkWrapper>
        </NavItem>
      ))}
    </NavList>
  );
};
